import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { colors, typography } from '../styles/theme';

const Spinner = () => (
  <CircularProgress 
    size={20} 
    thickness={3}
    sx={{ 
      color: colors.text.light,
    }} 
  />
);

interface TWButtonProps extends Omit<ButtonProps, 'startIcon' | 'endIcon'> {
  loading?: boolean;
  label?: string;
  startIcon?: React.ReactNode;
  buttonType?: 'primary' | 'secondary';
}

const TWButton: React.FC<TWButtonProps> = ({
  loading = false,
  disabled = false,
  startIcon,
  label,
  buttonType = 'primary',
  sx,
  children,
  ...rest
}) => {
  const isPrimary = buttonType === 'primary';
  const hasIcon = startIcon || loading;
  const isDisabledOnly = disabled && !loading;
  
  return (
    <Button
      variant={isPrimary ? 'contained' : 'outlined'}
      disableElevation
      disabled={disabled || loading}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: hasIcon ? '10px' : 0,
        padding: '0 24px',
        minHeight: '48px',
        backgroundColor:
          isDisabledOnly
            ? colors.text.secondary
            : isPrimary
            ? colors.primary
            : colors.background.card,
        color: colors.text.light,
        borderRadius: 0,
        boxShadow:
          '0 3px 1px -2px rgba(0, 0, 0, 0.20), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)',
        border: isPrimary ? 'none' : `1.5px solid ${colors.primary}`,
        textTransform: 'none',
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.medium,
        minWidth: 200,
        '&:hover': {
          backgroundColor: isPrimary ? colors.primaryHover : colors.background.disabled,
        },
        '&:active': {
          backgroundColor: isPrimary ? '#002631' : '#ECECEC',
        },
        '&.Mui-disabled': {
          backgroundColor: loading ? colors.primary : colors.text.secondary,
          color: colors.text.light,
          borderColor: loading ? colors.primary : colors.text.secondary,
          opacity: loading ? 0.85 : 1,
        },
        ...sx,
      }}
      {...rest}
      startIcon={loading ? <Spinner /> : startIcon}
      endIcon={undefined}
    >
      {label || children}
    </Button>
  );
};

export default TWButton;
